import React, { useState } from "react";
import { Typography } from "antd";
import { RightOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const PortalStyleWatermark = ({ modules, onNavigate }) => {
    const [hoveredModule, setHoveredModule] = useState(null);
    const numRows = Math.ceil(modules.length / 4);

    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gridTemplateRows: `repeat(${numRows}, 1fr)`,
                gap: "16px",
                height: "100%",
                padding: "8px",
            }}
        >
            {modules.map((mod) => (
                <div
                    key={mod.key}
                    style={{
                        position: "relative",
                        background: "#fff", // Clean white background
                        borderRadius: "12px",
                        // border: moved to overlay
                        overflow: "hidden",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        boxShadow: hoveredModule === mod.key ? "0 12px 24px rgba(0,0,0,0.08)" : "0 2px 8px rgba(0,0,0,0.03)",
                        cursor: "default",
                    }}
                    onMouseEnter={() => setHoveredModule(mod.key)}
                    onMouseLeave={() => setHoveredModule(null)}
                >
                    {/* Watermark Icon (The "Background" Effect) */}
                    <div
                        style={{
                            position: "absolute",
                            bottom: "-5px", // Moving it way down
                            right: "-5px", // Moving it way right
                            width: "180px", // Explicit large size
                            height: "180px",
                            color: mod.borderColor, // Always use borderColor for visibility
                            opacity: 0.07,
                            transform: "rotate(-15deg)",
                            pointerEvents: "none",
                            zIndex: 0,
                            transition: "all 0.5s ease",
                            transformOrigin: "bottom right",
                            scale: hoveredModule === mod.key ? "1.1" : "1",
                            display: "flex", // Flex to center/stretch content
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {/* 
                           CRITICAL FIX: 
                           Use React.cloneElement to override the specific 'fontSize: 32' 
                           that was defined in the parent config array.
                           We force it to fit our container.
                        */}
                        {React.cloneElement(mod.icon, {
                            style: {
                                fontSize: "240px",
                                width: "100%",
                                height: "100%",
                                color: "currentColor", // Ensure it inherits the faded color
                            },
                        })}
                    </div>

                    {/* Main Content */}
                    <div
                        style={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between", // Better vertical distribution
                            padding: "24px",
                            position: "relative",
                            zIndex: 1,
                        }}
                    >
                        {/* Header Section */}
                        <div>
                            <div style={{ display: "flex", alignItems: "center", marginBottom: "12px" }}>
                                {/* Vertical Accent Bar */}
                                <div
                                    style={{
                                        width: "4px",
                                        height: "24px",
                                        background: mod.borderColor,
                                        borderRadius: "2px",
                                        marginRight: "12px",
                                    }}
                                />
                                <Title
                                    level={4} // Slightly smaller but clearer hierarchy
                                    style={{
                                        margin: 0,
                                        fontWeight: 800,
                                        color: "#1f1f1f",
                                        fontSize: "24px",
                                        letterSpacing: "0.5px",
                                        lineHeight: 1.1,
                                    }}
                                >
                                    {mod.title.split("(")[0]}
                                </Title>
                            </div>

                            <Text
                                style={{
                                    fontSize: "13px",
                                    color: "#6b7280", // Softer gray
                                    lineHeight: "1.6",
                                    display: "block",
                                    maxWidth: "75%",
                                    paddingLeft: "16px", // Indent to align with text start
                                }}
                            >
                                {mod.description}
                            </Text>
                        </div>

                        {/* Key Metrics Display - Bottom Aligned */}
                        {mod.metrics && (
                            <div
                                style={{
                                    display: "flex",
                                    gap: "24px",
                                    paddingTop: "16px",
                                    paddingLeft: "16px",
                                    marginTop: "auto", // Push to bottom if content is short
                                }}
                            >
                                {mod.metrics.map((metric, i) => (
                                    <div key={i}>
                                        <div
                                            style={{
                                                fontSize: "24px",
                                                fontWeight: 700,
                                                color: mod.borderColor, // Use brand color for importance
                                                lineHeight: "1.0",
                                                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
                                                fontVariantNumeric: "tabular-nums",
                                            }}
                                        >
                                            {metric.value}
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "4px", fontWeight: 500 }}>{metric.label}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Hover Menu (Slide Up) - Kept consistent interaction */}
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            background: "rgba(255,255,255,0.96)", // High opacity to cover watermark
                            backdropFilter: "blur(5px)",
                            padding: "16px",
                            display: "flex",
                            flexDirection: "column",
                            transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                            transform: hoveredModule === mod.key ? "translateY(0)" : "translateY(100%)",
                            zIndex: 10,
                            overflowY: "auto",
                        }}
                    >
                        <div
                            style={{
                                borderBottom: `2px solid ${mod.borderColor}`,
                                marginBottom: "12px",
                                paddingBottom: "8px",
                                fontWeight: 600,
                                fontSize: "14px",
                                color: "#262626",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <span>{mod.title}</span>
                            {/* Small icon in menu header */}
                            <span style={{ fontSize: "16px", color: mod.borderColor }}>{mod.icon}</span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            {mod.items.map((item, idx) => (
                                <div
                                    key={idx}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onNavigate(item.path);
                                    }}
                                    className="portal-link-item"
                                    style={{
                                        padding: "10px 14px", // Slighly more compact than classic
                                        borderRadius: "8px",
                                        background: "#f7f7f7",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "12px",
                                        fontSize: "14px",
                                        border: "1px solid transparent",
                                        transition: "all 0.2s",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = mod.color;
                                        e.currentTarget.style.borderColor = mod.borderColor;
                                        e.currentTarget.style.color = "#262626";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = "#f7f7f7";
                                        e.currentTarget.style.borderColor = "transparent";
                                        e.currentTarget.style.color = "#595959";
                                    }}
                                >
                                    <span style={{ fontSize: "16px", color: mod.borderColor }}>{item.icon}</span>
                                    <span>{item.label}</span>
                                    <RightOutlined style={{ marginLeft: "auto", fontSize: "12px", opacity: 0.4 }} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Border Overlay: Ensures border sits on top of child backgrounds for clean corners */}
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            borderRadius: "12px",
                            border: `1px solid ${hoveredModule === mod.key ? mod.borderColor : "#f0f0f0"}`,
                            pointerEvents: "none",
                            zIndex: 20,
                            transition: "border-color 0.3s ease",
                        }}
                    />
                </div>
            ))}
        </div>
    );
};

export default PortalStyleWatermark;
